const {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  startAfter,
  serverTimestamp,
  updateDoc,
  where
} = require('firebase/firestore');
const { timedFirestoreCall, timedFirestoreSubscription } = require('./firestore-timing');
const {
  cacheState,
  cacheStatus,
  cacheTtlMilliseconds,
  isCacheablePagePath,
  renderedAtMilliseconds
} = require('./cache');
const { appApiUrl } = require('./api-url');
const { cacheVersionForPath, nextSiteCacheVersion } = require('../../../shared/cache-version');
const { rendererMetricNames } = require('../../../shared/renderer-check');
const defaultUsagePeriodHours = 7 * 24;
const routeUsagePeriodHours = 7 * 24;
const sitePageSize = 20;
const routeEventPageSize = 20;

function usagePeriodStart(periodHours) {
  return new Date(Date.now() - periodHours * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 13);
}

function siteData(form) {
  return {
    name: form.name,
    origin: form.origin.replace(/\/$/, ''),
    renderOptions: {
      renderer: form.renderer || 'chromium',
      cacheTtl: cacheTtlMilliseconds(Number(form.cacheTtl) * 1000),
      wait: {
        minimum: Number(form.minimum),
        quiet: Number(form.quiet)
      },
      capture: form.captures
        .filter(capture => capture.url.trim())
        .map(capture => ({
          url: capture.url.trim(),
          methods: [capture.method],
          match: capture.match
        })),
      ignoredRoutes: form.ignoredRoutes
        .map(route => route.trim())
        .filter(Boolean)
    }
  };
}

async function loadSitesPage(db, user, cursor = null) {
  const constraints = [where('ownerId', '==', user.uid)];
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(sitePageSize + 1));

  let result;
  try {
    result = await timedFirestoreCall('query.get', 'sites', () =>
      getDocs(query(collection(db, 'sites'), ...constraints)));
  } catch (reason) {
    console.error('Unable to load owned sites from Firestore.', reason);
    throw reason;
  }

  const hasNextPage = result.docs.length > sitePageSize;
  const pageDocuments = result.docs.slice(0, sitePageSize);
  return {
    sites: pageDocuments.map(site => ({ id: site.id, ...site.data() })),
    cursor: hasNextPage ? pageDocuments[pageDocuments.length - 1] : null,
    hasNextPage
  };
}

async function loadSite(db, siteId) {
  const snapshot = await timedFirestoreCall('document.get', `sites/${siteId}`, () =>
    getDoc(doc(db, 'sites', siteId)));

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() };
}

function subscribeToSite(db, site, onUpdate, onError) {
  return timedFirestoreSubscription(
    `sites/${site.id}`,
    doc(db, 'sites', site.id),
    snapshot => {
      if (snapshot.exists()) {
        onUpdate({ id: snapshot.id, ...snapshot.data() });
      }
    },
    onError
  );
}

function subscribeToHomepageCache(db, site, onUpdate, onError) {
  const cacheVersion = cacheVersionForPath(site, '/');
  const key = base64UrlEncode(`${site.id}:${cacheVersion}:/`);

  return timedFirestoreSubscription(
    `renderCache/${key}`,
    doc(db, 'renderCache', key),
    snapshot => onUpdate(snapshot.exists() && snapshot.data().ready !== false),
    onError
  );
}

async function saveSite(db, user, form, invalidateCache = false) {
  const site = siteData(form);
  let siteId = form.id;

  if (siteId) {
    const reference = doc(db, 'sites', siteId);
    if (invalidateCache) {
      await timedFirestoreCall('transaction.update', `sites/${siteId}`, () =>
        runTransaction(db, async transaction => {
          const current = await transaction.get(reference);
          transaction.update(reference, {
            ...site,
            cacheVersion: nextSiteCacheVersion(current.data())
          });
        }));
    } else {
      await timedFirestoreCall('document.update', `sites/${siteId}`, () => updateDoc(reference, site));
    }
  } else {
    const reference = await timedFirestoreCall('collection.add', 'sites', () =>
      addDoc(collection(db, 'sites'), {
        ...site,
        ownerId: user.uid,
        domains: [],
        createdAt: serverTimestamp()
      }));
    siteId = reference.id;
  }

  return { siteId, site };
}

async function clearSiteCache(db, siteId) {
  const reference = doc(db, 'sites', siteId);
  await timedFirestoreCall('transaction.update', `sites/${siteId}`, () =>
    runTransaction(db, async transaction => {
      const current = await transaction.get(reference);
      transaction.update(reference, { cacheVersion: nextSiteCacheVersion(current.data()) });
    }));
}

async function updateCacheWebhook(user, siteId, method) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/sites/${siteId}/cache-webhook`, {
    method,
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to update cache webhook');
  }

  return result;
}

async function createCacheWebhook(user, siteId) {
  return updateCacheWebhook(user, siteId, 'POST');
}

async function revokeCacheWebhook(user, siteId) {
  return updateCacheWebhook(user, siteId, 'DELETE');
}

async function clearRouteCache(user, siteId, path) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/sites/${siteId}/cache-route`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify({ path })
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to clear route cache');
  }

  return result;
}

async function removeSite(db, siteId) {
  await timedFirestoreCall('document.delete', `sites/${siteId}`, () =>
    deleteDoc(doc(db, 'sites', siteId)));
}

async function provisionDomain(user, siteId, domain) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/sites/${siteId}/domains`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify({ domain })
  });

  if (response.ok) {
    return;
  }

  const result = await response.json();
  throw new Error(result.error || 'Unable to provision domain');
}

async function removeDomain(user, siteId, domain) {
  const token = await user.getIdToken();
  const response = await fetch(
    `${appApiUrl}/api/sites/${siteId}/domains/${encodeURIComponent(domain)}`,
    {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` }
    }
  );

  if (response.ok) {
    return;
  }

  const result = await response.json();
  throw new Error(result.error || 'Unable to remove domain');
}

async function testDomain(user, siteId, domain) {
  const token = await user.getIdToken();
  const response = await fetch(
    `${appApiUrl}/api/sites/${siteId}/domains/${encodeURIComponent(domain)}/test`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` }
    }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to test domain');
  }

  return result;
}

async function updateRouteParameter(user, siteId, path, parameter, action = 'include') {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/sites/${siteId}/route-settings`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify({ path, parameter, action })
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to update route settings');
  }

  return result;
}

async function loadRouteScreenshotSettings(user, siteId, path) {
  const token = await user.getIdToken();
  const url = new URL(`${appApiUrl}/api/sites/${siteId}/route-screenshot-settings`, window.location.origin);
  url.searchParams.set('path', path);
  const response = await fetch(url, {
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to load route screenshot settings');
  }

  return result;
}

async function routeRendererSettings(user, siteId, path, renderer) {
  const token = await user.getIdToken();
  const url = new URL(`${appApiUrl}/api/sites/${siteId}/route-renderer`, window.location.origin);
  const writing = renderer !== undefined;
  if (!writing) url.searchParams.set('path', path);
  const response = await fetch(url, {
    method: writing ? 'POST' : 'GET',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    ...(writing ? { body: JSON.stringify({ path, renderer }) } : {})
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Unable to update route renderer');
  return result;
}

async function updateRouteScreenshotVisibility(user, siteId, path, publicScreenshot) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/sites/${siteId}/route-screenshot-settings`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify({ path, publicScreenshot })
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to update route screenshot visibility');
  }

  return result;
}

async function updatePreviewSlug(user, siteId, previewSlug) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/sites/${siteId}/preview-slug`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify({ previewSlug })
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to update preview subdomain');
  }

  return result;
}

async function loadSiteSpendEstimate(user, siteId) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/sites/${siteId}/cost-estimate`, {
    headers: { authorization: `Bearer ${token}` }
  });

  if (response.ok) {
    return response.json();
  }

  const result = await response.json();
  throw new Error(result.error || 'Unable to calculate site spend');
}

async function loadRouteEvents(db, site, path) {
  const route = new URL(path || '/', window.location.origin).pathname;
  const events = await timedFirestoreCall('query.get', `siteMetrics/${site.id}/events`, () =>
    getDocs(query(
      collection(db, 'siteMetrics', site.id, 'events'),
      where('path', '==', route),
      orderBy('occurredAt', 'desc'),
      limit(50)
    )));

  return events.docs.map(event => ({ id: event.id, ...event.data() }));
}

async function loadRouteEventPage(db, site, path, cursor = null) {
  const route = new URL(path || '/', window.location.origin).pathname;
  const constraints = [
    where('path', '==', route),
    orderBy('occurredAt', 'desc')
  ];
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(routeEventPageSize + 1));

  const snapshot = await timedFirestoreCall('query.get', `siteMetrics/${site.id}/events`, () =>
    getDocs(query(
      collection(db, 'siteMetrics', site.id, 'events'),
      ...constraints
    )));
  const documents = snapshot.docs.slice(0, routeEventPageSize);

  return {
    events: documents.map(event => ({ id: event.id, ...event.data() })),
    cursor: snapshot.docs.length > routeEventPageSize
      ? documents[documents.length - 1]
      : null,
    hasMore: snapshot.docs.length > routeEventPageSize
  };
}

async function loadRouteActivity(db, site, path) {
  const route = new URL(path || '/', window.location.origin).pathname;
  const periodStart = usagePeriodStart(routeUsagePeriodHours);
  const snapshot = await timedFirestoreCall('query.get', `siteMetrics/${site.id}/routes`, () =>
    getDocs(query(
      collection(db, 'siteMetrics', site.id, 'routes'),
      where('path', '==', route),
      where('hour', '>=', periodStart),
      orderBy('hour')
    )));

  return snapshot.docs.map(document => ({ id: document.id, ...document.data() }));
}

async function loadRouteTimeline(db, site, path) {
  const [page, activity] = await Promise.all([
    loadRouteEventPage(db, site, path),
    loadRouteActivity(db, site, path)
  ]);

  return { ...page, activity };
}

async function loadRouteCacheInfo(db, site, path) {
  const cacheVersion = cacheVersionForPath(site, path);
  const key = base64UrlEncode(`${site.id}:${cacheVersion}:${path}`);
  const snapshot = await timedFirestoreCall('document.get', `renderCache/${key}`, () =>
    getDoc(doc(db, 'renderCache', key)));

  return snapshot.exists() ? snapshot.data() : null;
}

async function loadRouteScreenshot(user, siteId, path) {
  const token = await user.getIdToken();
  const url = new URL(`${appApiUrl}/api/sites/${siteId}/route-screenshot`, window.location.origin);
  url.searchParams.set('path', path);
  const response = await fetch(url, {
    headers: { authorization: `Bearer ${token}` }
  });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const result = await response.json();
    throw new Error(result.error || 'Unable to load route screenshot');
  }

  return response.blob();
}

function base64UrlEncode(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function loadSiteStats(db, site, usagePeriodHours = defaultUsagePeriodHours) {
  let result;

  try {
    result = await timedFirestoreCall('query.get', `renderCache (siteId=${site.id})`, () =>
      getDocs(query(
        collection(db, 'renderCache'),
        where('siteId', '==', site.id)
      )));
  } catch (reason) {
    console.error(`Unable to load stats for site ${site.id} from Firestore.`, reason);
    throw reason;
  }
  const currentCacheVersion = site.cacheVersion || 0;
  const cachedPages = result.docs
    .map(item => item.data())
    .filter(page => isCacheablePagePath(page.path || '/'))
    .filter(page => page.cacheVersion === currentCacheVersion ||
      (currentCacheVersion === 0 && page.cacheVersion === undefined));
  const cacheTtl = cacheTtlMilliseconds(site.renderOptions?.cacheTtl);
  return {
    pages: cachedPages.length,
    bytes: cachedPages.reduce((total, page) => total + (page.sizeBytes || 0), 0),
    homepageCached: cachedPages.some(page => page.path === '/'),
    slowestPages: cachedPages
      .sort((left, right) =>
        (right.renderDurationMs || right.timeToLastRequestFinishedMs || 0) -
        (left.renderDurationMs || left.timeToLastRequestFinishedMs || 0))
      .slice(0, 10),
    recentlyRenderedPages: cachedPages
      .map(page => ({
        ...page,
        cacheStatus: cacheStatus(page, cacheTtl),
        cacheState: cacheState(page, cacheTtl)
      }))
      .sort((left, right) =>
        renderedAtMilliseconds(right) - renderedAtMilliseconds(left))
      .slice(0, 10),
    // Hourly and route metrics are populated by subscribeToSiteUsage below.
    // Avoid fetching them here and then immediately fetching them again when
    // the live listeners start.
    ...usageFromMetricDocuments([]),
    routes: [],
    usagePeriodHours
  };
}

function aggregateRouteMetrics(documents) {
  const routes = new Map();

  for (const document of documents) {
    const metrics = document.data();
    const current = routes.get(metrics.path) || {
      path: metrics.path,
      renderDurationMs: 0,
      renders: 0,
      renderFailures: 0,
      requests: 0,
      responseBodyBytes: 0,
      renderReasons: {}
    };
    current.renderDurationMs += metrics.renderDurationMs || 0;
    current.renders += metrics.renders || 0;
    current.renderFailures += metrics.renderFailures || 0;
    current.requests += metrics.requests || 0;
    current.responseBodyBytes += metrics.responseBodyBytes || 0;
    for (const name of rendererMetricNames) current[name] = (current[name] || 0) + (metrics[name] || 0);
    for (const [name, value] of Object.entries(metrics)) {
      if (/^renders[A-Z]/.test(name)) {
        current.renderReasons[name.slice('renders'.length)] =
          (current.renderReasons[name.slice('renders'.length)] || 0) + value;
      }
    }
    routes.set(metrics.path, current);
  }

  return [...routes.values()]
    .filter(route => isCacheablePagePath(route.path))
    .sort((left, right) => right.responseBodyBytes - left.responseBodyBytes);
}

function usageFromMetricDocuments(hourlyDocuments) {
  const usage = hourlyDocuments.reduce((total, hour) => {
    const metrics = typeof hour.data === 'function' ? hour.data() : hour;

    return {
      cacheHits: total.cacheHits + (metrics.cacheHits || 0),
      cacheMisses: total.cacheMisses + (metrics.cacheMisses || 0),
      pageHits: total.pageHits + (metrics.pageHits || 0),
      proxiedRequests: total.proxiedRequests + (metrics.proxiedRequests || 0),
      proxyDurationMs: total.proxyDurationMs + (metrics.proxyDurationMs || 0),
      requestBodyBytes: total.requestBodyBytes + (metrics.requestBodyBytes || 0),
      renderDurationMs: total.renderDurationMs + (metrics.renderDurationMs || 0),
      workerComputeMs: total.workerComputeMs + (metrics.workerComputeMs || 0),
      renders: total.renders + (metrics.renders || 0),
      staleCacheHits: total.staleCacheHits + (metrics.staleCacheHits || 0),
      responseBodyBytes: total.responseBodyBytes + (metrics.responseBodyBytes || 0),
      validationDurationMs: total.validationDurationMs + (metrics.validationDurationMs || 0),
      validations: total.validations + (metrics.validations || 0)
    };
  }, {
    cacheHits: 0,
    cacheMisses: 0,
    pageHits: 0,
    proxiedRequests: 0,
    proxyDurationMs: 0,
    requestBodyBytes: 0,
    renderDurationMs: 0,
    workerComputeMs: 0,
    renders: 0,
    staleCacheHits: 0,
    responseBodyBytes: 0,
    validationDurationMs: 0,
    validations: 0
  });
  const hourlyUsage = hourlyDocuments
    .map(hour => typeof hour.data === 'function' ? hour.data() : hour)
    .sort((left, right) => (left.hour || '').localeCompare(right.hour || ''));

  return {
    usage,
    hourlyUsage
  };
}

function subscribeToSiteUsage(db, site, usagePeriodHours, onUpdate, onError) {
  let hourlyDocuments = [];
  let routeDocuments = [];
  const periodStart = usagePeriodStart(usagePeriodHours);
  const routePeriodStart = usagePeriodStart(routeUsagePeriodHours);

  function publish() {
    onUpdate({
      ...usageFromMetricDocuments(hourlyDocuments),
      routes: aggregateRouteMetrics(routeDocuments),
      usagePeriodHours,
      routeUsagePeriodHours
    });
  }

  const stopHourly = timedFirestoreSubscription(
    `siteMetrics/${site.id}/hours`,
    query(
      collection(db, 'siteMetrics', site.id, 'hours'),
      where('hour', '>=', periodStart)
    ),
    snapshot => {
      hourlyDocuments = snapshot.docs;
      publish();
    },
    reason => {
      console.error(`Unable to stream hourly usage for site ${site.id}.`, reason);
      onError(reason);
    }
  );
  const stopRoutes = timedFirestoreSubscription(
    `siteMetrics/${site.id}/routes`,
    query(
      collection(db, 'siteMetrics', site.id, 'routes'),
      where('hour', '>=', routePeriodStart)
    ),
    snapshot => {
      routeDocuments = snapshot.docs;
      publish();
    },
    reason => {
      console.error(`Unable to stream route usage for site ${site.id}.`, reason);
      onError(reason);
    }
  );

  return () => {
    stopHourly();
    stopRoutes();
  };
}

module.exports = {
  clearRouteCache,
  clearSiteCache,
  createCacheWebhook,
  loadSite,
  subscribeToSite,
  subscribeToHomepageCache,
  loadRouteEvents,
  loadRouteEventPage,
  loadRouteTimeline,
  loadRouteCacheInfo,
  loadRouteScreenshotSettings,
  loadRouteScreenshot,
  loadSitesPage,
  sitePageSize,
  loadSiteSpendEstimate,
  loadSiteStats,
  provisionDomain,
  removeDomain,
  testDomain,
  updateRouteParameter,
  routeRendererSettings,
  updateRouteScreenshotVisibility,
  updatePreviewSlug,
  removeSite,
  revokeCacheWebhook,
  saveSite,
  subscribeToSiteUsage
};
